import type { AvailableTag } from 'apiConnector/life-ring';
import { lifeRingApiConnector } from 'apiConnector/life-ring';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { toApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import { useCallback, useEffect, useReducer, useRef } from 'react';
import type { Subscription } from 'rxjs';
import { httpClient } from 'utils/http/httpClient';
import type { HttpError } from 'utils/http/httpClient.model';

export type FetchStatus = 'loading' | 'success' | 'error';
export type FetchError = ApiErrorMessage | 'unauthorized';

function isForbidden(httpErrorCode: number) {
  return httpErrorCode === 401 || httpErrorCode === 403;
}

type FetchState =
  | { status: 'loading'; data: null; error: null }
  | { status: 'success'; data: Readonly<AvailableTag[]>; error: null }
  | { status: 'error'; data: null; error: FetchError };

type FetchAction =
  | { type: 'fetch-start' }
  | { type: 'fetch-success'; data: Readonly<AvailableTag[]> }
  | { type: 'fetch-error'; error: FetchError };

function fetchAvailableTagsReducer(state: FetchState, action: FetchAction): FetchState {
  switch (action.type) {
    case 'fetch-start':
      return { status: 'loading', data: null, error: null };
    case 'fetch-success':
      return { status: 'success', data: action.data, error: null };
    case 'fetch-error':
      return { status: 'error', data: null, error: action.error };
    default:
      return state;
  }
}

const api = lifeRingApiConnector(httpClient);
let cachedAvailableTags: Readonly<AvailableTag[]> | null = null;

function getInitialFetchState(isInternal: boolean): FetchState {
  if (cachedAvailableTags) {
    return { status: 'success', data: cachedAvailableTags, error: null };
  }

  if (isInternal) {
    return { status: 'loading', data: null, error: null };
  }

  return { status: 'success', data: [], error: null };
}

export function useFetchHelpCenterAvailableTags() {
  const isInternal = useIsInternalUser();
  const [state, dispatch] = useReducer(fetchAvailableTagsReducer, isInternal, getInitialFetchState);
  const subscriptionRef = useRef<Subscription | null>(null);

  const fetchAvailableTags = useCallback(() => {
    if (!isInternal) {
      return;
    }

    dispatch({ type: 'fetch-start' });
    subscriptionRef.current?.unsubscribe();
    subscriptionRef.current = api.getAvailableTags().subscribe({
      next(data) {
        cachedAvailableTags = data;
        dispatch({ type: 'fetch-success', data });
      },
      error(error: HttpError) {
        if (isForbidden(error.status)) {
          dispatch({ type: 'fetch-error', error: 'unauthorized' });
          return;
        }

        dispatch({ type: 'fetch-error', error: toApiErrorMessage(error) });
      },
    });
  }, [isInternal]);

  const handleRetry = useCallback(() => {
    fetchAvailableTags();
  }, [fetchAvailableTags]);

  useEffect(() => {
    if (cachedAvailableTags) {
      return;
    }
    fetchAvailableTags();

    return function cleanup() {
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, [fetchAvailableTags]);

  const isLoading = state.status === 'loading';
  const isError = state.status === 'error';
  const isSuccess = state.status === 'success';

  return {
    ...state,
    isError,
    isLoading,
    isSuccess,
    onRetry: handleRetry,
  };
}
